import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
    CategoryScale,
    Chart as ChartJS,
    type Chart,
    type ChartData,
    type ChartDataset,
    type ChartOptions,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    type ScriptableContext,
    Title,
    Tooltip,
} from 'chart.js';

import { cleanupCustomTooltip, createCustomTooltip } from '@/shared/lib/chart';
import { useXLMediaQuery } from '@/shared/lib/hooks';
import { roundToDecimals } from '@/shared/lib/math';
import { Card } from '@/shared/ui/card';
import { TitleWithHelp } from '@/shared/ui/title-with-help';

import { getResolvedPatternColors } from '../../lib/pattern-colors';
import {
    createDataPoints,
    getDirectionalProperty,
    parseArrayProperty,
} from '../../lib/pattern-manipulator';
import type { Pattern } from '../../model/types/pattern';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);

export interface PatternVolatilityChartProps {
    patterns: Pattern | Pattern[];
    title: string;
    chartId: string;
    colorsById?: Record<string, string>;
    isAggregationMode?: boolean;
}

// Strict typing for chart.js fill options, avoiding 'any'
interface CustomFillOptions {
    target: 'origin' | 'start' | 'end' | number | string | undefined;
    above: (ctx: ScriptableContext<'line'>) => string | CanvasGradient;
    below: (ctx: ScriptableContext<'line'>) => string | CanvasGradient;
}

// Convert HEX to rgba with given alpha
const hexToRgba = (hex: string, alpha: number): string => {
    const clean = hex.replace('#', '');
    const r = Number.parseInt(clean.slice(0, 2), 16);
    const g = Number.parseInt(clean.slice(2, 4), 16);
    const b = Number.parseInt(clean.slice(4, 6), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Create a vertical gradient function for area fill based on a base color
const createAreaFill =
    (baseColor: string) =>
    (ctx: ScriptableContext<'line'>): string | CanvasGradient => {
        if (!ctx.chart.chartArea) {
            return hexToRgba(baseColor, 0.3);
        }

        const { ctx: canvasCtx, chartArea } = ctx.chart;
        const gradient = canvasCtx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom,
        );

        gradient.addColorStop(0, hexToRgba(baseColor, 0.8));
        gradient.addColorStop(0.6, hexToRgba(baseColor, 0.25));
        gradient.addColorStop(1, hexToRgba(baseColor, 0));

        return gradient;
    };

const transparentFill = (_ctx: ScriptableContext<'line'>): string =>
    'rgba(0,0,0,0)';

const palette: readonly [string, string, string, string, string, string] = [
    '#3B82F6',
    '#10B981',
    '#EF4444',
    '#F59E0B',
    '#8B5CF6',
    '#06B6D4',
] as const;

interface VolatilityEntry {
    id: string;
    values: number[];
    keptIndices: number[];
    atr: (number | null)[] | undefined;
    indice: (number | null)[] | undefined;
}

interface StatsRow {
    label: string;
    value: string;
    description: string;
}

interface HoverData {
    dataIndex: number;
}

interface PatternStatsData {
    id: string;
    displayName: string;
    stats: StatsRow[];
}

const PLACEHOLDER_VALUE = '--';

// Translation key constants
const VOLATILITY_PERCENT_KEY = 'patternStats.volatilityPercent';
const VOLATILITY_PERCENT_DESC_KEY = 'patternStats.volatilityPercentDesc';
const AVERAGE_TRUE_RANGE_KEY = 'patternStats.averageTrueRange';
const AVERAGE_TRUE_RANGE_DESC_KEY = 'patternStats.averageTrueRangeDesc';
const AVERAGE_TRUE_RANGE_UNIT_KEY = 'patternStats.averageTrueRangeUnit';
const UNCERTAINTY_INDEX_KEY = 'patternStats.uncertaintyIndex';
const UNCERTAINTY_INDEX_DESC_KEY = 'patternStats.uncertaintyIndexDesc';

const createDynamicStats = (
    entry: VolatilityEntry,
    hoverData: HoverData | null,
    t: (key: string) => string,
): StatsRow[] => {
    if (!hoverData) {
        return [
            {
                label: t(VOLATILITY_PERCENT_KEY),
                value: PLACEHOLDER_VALUE,
                description: t(VOLATILITY_PERCENT_DESC_KEY),
            },
            {
                label: t(AVERAGE_TRUE_RANGE_KEY),
                value: PLACEHOLDER_VALUE,
                description: t(AVERAGE_TRUE_RANGE_DESC_KEY),
            },
            {
                label: t(UNCERTAINTY_INDEX_KEY),
                value: PLACEHOLDER_VALUE,
                description: t(UNCERTAINTY_INDEX_DESC_KEY),
            },
        ];
    }

    const filteredIdx = hoverData.dataIndex;
    const origIdx = entry.keptIndices?.[filteredIdx] ?? 0;

    const vValue = entry.values?.[filteredIdx];
    const atrValue = entry.atr?.[origIdx];
    const idxValue = entry.indice?.[origIdx];

    return [
        {
            label: t(VOLATILITY_PERCENT_KEY),
            value:
                vValue !== null && vValue !== undefined
                    ? `${roundToDecimals(vValue)}%`
                    : '--',
            description: t(VOLATILITY_PERCENT_DESC_KEY),
        },
        {
            label: t(AVERAGE_TRUE_RANGE_KEY),
            value:
                atrValue !== null && atrValue !== undefined
                    ? `${roundToDecimals(atrValue)} ${t(AVERAGE_TRUE_RANGE_UNIT_KEY)}`
                    : '--',
            description: t(AVERAGE_TRUE_RANGE_DESC_KEY),
        },
        {
            label: t(UNCERTAINTY_INDEX_KEY),
            value:
                idxValue !== null && idxValue !== undefined
                    ? `${roundToDecimals(idxValue)}%`
                    : '--',
            description: t(UNCERTAINTY_INDEX_DESC_KEY),
        },
    ];
};

const aggregateStats = (
    entries: Array<{ stats: StatsRow[]; displayName: string }>,
): StatsRow[] => {
    if (entries.length === 0) return [];

    // Group stats by label
    const statsByLabel = new Map<string, StatsRow[]>();

    entries.forEach((entry) => {
        entry.stats.forEach((stat) => {
            if (!statsByLabel.has(stat.label)) {
                statsByLabel.set(stat.label, []);
            }

            statsByLabel.get(stat.label)!.push(stat);
        });
    });

    // Aggregate each stat group
    const aggregatedStats: StatsRow[] = [];

    statsByLabel.forEach((stats, label) => {
        // Count valid stats (those not showing PLACEHOLDER_VALUE)
        const validStats = stats.filter(
            (stat) => stat.value !== PLACEHOLDER_VALUE,
        );

        if (validStats.length === 0) {
            // If no valid stats, use the placeholder structure from first stat
            const firstStat = stats[0];

            if (firstStat) {
                aggregatedStats.push({
                    label: firstStat.label,
                    value: PLACEHOLDER_VALUE,
                    description: firstStat.description,
                });
            }

            return;
        }

        // For percentage values, remove % sign and convert to number
        const firstValidStat = validStats[0];

        if (!firstValidStat) return;

        const isPercentage = firstValidStat.value.includes('%');
        let totalValue = 0;

        validStats.forEach((stat) => {
            if (isPercentage) {
                totalValue += Number.parseFloat(stat.value.replace('%', ''));
            } else {
                totalValue += Number.parseFloat(stat.value);
            }
        });

        const avgValue = totalValue / validStats.length;
        const formattedValue = isPercentage
            ? `${roundToDecimals(avgValue)}%`
            : String(roundToDecimals(avgValue));

        // Use the description from the first stat
        const { description } = firstValidStat;

        aggregatedStats.push({
            label,
            value: formattedValue,
            description,
        });
    });

    return aggregatedStats;
};

const buildPatternStatsData = (
    entries: VolatilityEntry[],
    patternList: Pattern[],
    hoverData: HoverData | null,
    isMulti: boolean,
    isAggregationMode: boolean,
    t: (key: string) => string,
): PatternStatsData[] => {
    // First, create individual pattern entries with their stats
    const patternEntries = entries.map((entry, index) => {
        const pattern = patternList[index];
        const displayName =
            (pattern as Partial<Pattern>).pattern_name ??
            (pattern as Partial<Pattern>).unique_id ??
            entry.id;

        return {
            id: entry.id,
            displayName,
            stats: createDynamicStats(entry, hoverData, t),
        };
    });

    if (isAggregationMode && patternEntries.length > 1) {
        // In aggregation mode, aggregate the stats but keep all chart lines
        const aggregatedStats = aggregateStats(patternEntries);
        const aggregatedName = patternEntries
            .map((e) => e.displayName)
            .join(', ');

        return [
            {
                id: 'aggregated',
                displayName: aggregatedName,
                stats: aggregatedStats,
            },
        ];
    }

    if (isMulti && !isAggregationMode) {
        return patternEntries;
    }

    return [{ id: '', displayName: '', stats: patternEntries[0]?.stats ?? [] }];
};

const getContainerStyles = () =>
    ({
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 12,
    }) as const;

const getChartStyles = (isAggregationMode: boolean) => ({
    minHeight: 300,
    width: isAggregationMode ? '100%' : undefined,
});

const getStatsContainerStyles = (
    isXL: boolean,
    isMulti: boolean,
    isAggregationMode: boolean,
) =>
    ({
        display: isXL && isMulti && !isAggregationMode ? 'grid' : 'flex',
        gridTemplateColumns:
            isXL && isMulti && !isAggregationMode ? '1fr 1fr' : undefined,
        flexDirection:
            isXL && isMulti && !isAggregationMode ? undefined : 'column',
        gap: isXL ? 16 : 8,
        width: isAggregationMode ? '100%' : 'auto',
        overflow: 'visible',
        flexShrink: 0,
    }) as const;

export const PatternVolatilityChart = memo(
    ({
        patterns,
        title,
        chartId,
        colorsById,
        isAggregationMode = false,
    }: PatternVolatilityChartProps) => {
        const { t } = useTranslation('pattern-studio');
        const isXL = useXLMediaQuery();
        const [hoverData, setHoverData] = useState<HoverData | null>(() => ({
            dataIndex: 0,
        }));
        const chartRef = useRef<Chart<'line'> | null>(null);

        useEffect(
            () => () => {
                cleanupCustomTooltip();
            },
            [],
        );

        const patternList = useMemo(
            () => (Array.isArray(patterns) ? patterns : [patterns]),
            [patterns],
        );

        const entries = useMemo<VolatilityEntry[]>(
            () =>
                patternList.map((p) => {
                    const id = String(
                        (p as Partial<Pattern>)._id ??
                            (p as Partial<Pattern>).pattern_idx ??
                            (p as Partial<Pattern>).pattern_name ??
                            'pattern',
                    );

                    // Volatility
                    const volRaw =
                        parseArrayProperty<(number | null)[]>(
                            p as Partial<Pattern>,
                            'ts_volatility_post_mean_of_lists',
                        ) || [];

                    const reduced = volRaw.reduce<{
                        values: number[];
                        keptIndices: number[];
                    }>(
                        (acc, v, i) => {
                            if (v !== null && v !== undefined) {
                                acc.values.push((v as number) * 100);
                                acc.keptIndices.push(i);
                            }

                            return acc;
                        },
                        { values: [], keptIndices: [] },
                    );

                    const { values, keptIndices } = reduced;

                    // ATR
                    const atrParsed = parseArrayProperty<
                        (number | null)[] | undefined
                    >(p as Partial<Pattern>, 'ts_ATR_post_mean_of_lists');

                    const atr = Array.isArray(atrParsed)
                        ? atrParsed.map((value) =>
                              value !== null && value !== undefined
                                  ? Math.floor(value)
                                  : value,
                          )
                        : undefined;

                    // Uncertainty Indice
                    const indiceArrayRaw = getDirectionalProperty<
                        (number | null)[] | number[]
                    >(
                        p as Partial<Pattern>,
                        'ts_std_detrended_long_post_mean_of_lists',
                        'ts_std_detrended_short_post_mean_of_lists',
                    ) as (number | null)[];

                    const indiceArray = indiceArrayRaw.map((value) =>
                        value !== null && value !== undefined
                            ? value * 100
                            : value,
                    );

                    return {
                        id,
                        values,
                        keptIndices,
                        atr,
                        indice: indiceArray,
                    };
                }),
            [patternList],
        );

        const labels = createDataPoints(entries[0]?.values || []).map(
            (d) => d.timeLabel,
        );

        const isMulti = patternList.length > 1;
        const resolvedColors = useMemo(
            () => getResolvedPatternColors(patternList, isMulti),
            [patternList, isMulti],
        );

        const datasets = useMemo(
            () =>
                entries.map(({ values, id }, idx) => {
                    const appliedColor =
                        colorsById && colorsById[id]
                            ? colorsById[id]
                            : resolvedColors[idx] ??
                              (palette[idx % palette.length] as string);
                    const safeColor =
                        appliedColor ??
                        (palette[idx % palette.length] as string);
                    const above = createAreaFill(safeColor);

                    const pointBackgroundColorArray = values.map(
                        (_, pointIdx) => {
                            const isActive = hoverData?.dataIndex === pointIdx;

                            return isActive ? '#FFFFFF' : appliedColor;
                        },
                    );

                    const pointBorderColorArray = values.map((_, pointIdx) => {
                        const isActive = hoverData?.dataIndex === pointIdx;

                        return isActive ? '#FFFFFF' : appliedColor;
                    });

                    return {
                        label: id,
                        data: values,
                        tension: 0.4,
                        pointRadius: 2,
                        pointHoverRadius: 4,
                        pointBackgroundColor: pointBackgroundColorArray,
                        pointBorderColor: pointBorderColorArray,
                        pointBorderWidth: 1,
                        borderWidth: 2,
                        borderColor: appliedColor,
                        backgroundColor: 'transparent',
                        fill: {
                            target: 'origin',
                            above,
                            below: transparentFill,
                        } as CustomFillOptions,
                    };
                }),
            [entries, resolvedColors, colorsById, hoverData],
        );

        const data: ChartData<'line'> = {
            labels,
            datasets: datasets as unknown as ChartDataset<'line', number[]>[],
        };

        const options: ChartOptions<'line'> = {
            responsive: true,
            maintainAspectRatio: false,
            onHover: (_event, elements) => {
                if (elements.length > 0) {
                    const element = elements[0];

                    if (element) {
                        setHoverData({
                            dataIndex: element.index,
                        });
                    }
                }
            },
            plugins: {
                legend: {
                    display: datasets.length > 1,
                    position: 'top',
                    labels: {
                        usePointStyle: true,
                        padding: 15,
                        font: {
                            size: isXL ? 12 : 10,
                        },
                    },
                },
                title: { display: false },
                tooltip: {
                    enabled: false,
                    external: createCustomTooltip({
                        isDark: true,
                        title: t('patternStats.volatilityTitle'),
                        formatItems: (tooltipModel) =>
                            tooltipModel.dataPoints.map((point) => {
                                const { datasetIndex } = point;
                                const { dataIndex } = point;
                                const entry = entries[datasetIndex];

                                if (!entry) {
                                    return {
                                        color: point.dataset
                                            .borderColor as string,
                                        label: point.dataset.label || '',
                                        value: `${roundToDecimals(point.parsed.y)}%`,
                                    };
                                }

                                const filteredIdx = dataIndex;
                                const origIdx =
                                    entry.keptIndices?.[filteredIdx] ?? 0;
                                const vValue = entry.values?.[filteredIdx];
                                const atrValue = entry.atr?.[origIdx];
                                const idxValue = entry.indice?.[origIdx];

                                let valueText = `${roundToDecimals(vValue ?? 0)}%`;

                                if (
                                    atrValue !== null &&
                                    atrValue !== undefined
                                ) {
                                    valueText += ` | ATR: ${roundToDecimals(atrValue)}`;
                                }

                                if (
                                    idxValue !== null &&
                                    idxValue !== undefined
                                ) {
                                    valueText += ` | ${t('patternStats.uncertaintyIndex')}: ${roundToDecimals(idxValue)}%`;
                                }

                                return {
                                    color: point.dataset.borderColor as string,
                                    label: point.dataset.label || '',
                                    value: valueText,
                                };
                            }),
                    }),
                },
            },
            scales: {
                x: {
                    grid: { display: false },
                },
                y: {
                    grid: { color: 'rgba(255,255,255,0.1)' },
                },
            },
        };

        const patternStatsData = useMemo(
            () =>
                buildPatternStatsData(
                    entries,
                    patternList,
                    hoverData,
                    isMulti,
                    isAggregationMode,
                    t,
                ),
            [entries, patternList, hoverData, isMulti, isAggregationMode, t],
        );

        useEffect(() => {
            if (chartRef.current) {
                chartRef.current.update('none');
            }
        }, [hoverData]);

        return (
            <Card
                isWithoutPadding
                title={
                    <TitleWithHelp
                        helpId="PATTERN_VOLATILITY_CHART"
                        title={title}
                    />
                }
            >
                <div style={getContainerStyles()}>
                    <div style={getChartStyles(isAggregationMode)}>
                        <Line
                            ref={chartRef}
                            data={data}
                            id={chartId}
                            options={options}
                        />
                    </div>

                    <div
                        style={getStatsContainerStyles(
                            isXL,
                            isMulti,
                            isAggregationMode,
                        )}
                    >
                        {patternStatsData.map((patternData) => (
                            <div key={patternData.id || 'single'}>
                                {(isMulti || isAggregationMode) &&
                                    patternData.displayName && (
                                        <div
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                marginBottom: 8,
                                                color: 'rgba(255, 255, 255, 0.9)',
                                                borderBottom:
                                                    '1px solid rgba(255, 255, 255, 0.1)',
                                                paddingBottom: 4,
                                            }}
                                        >
                                            {patternData.displayName}
                                        </div>
                                    )}
                                {patternData.stats.length > 0 && (
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr',
                                            gap: 6,
                                            marginBottom:
                                                isMulti && !isAggregationMode
                                                    ? 16
                                                    : 0,
                                        }}
                                    >
                                        {patternData.stats.map((r, index) => (
                                            <div key={r.label}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                        gap: 8,
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    <span
                                                        style={{ opacity: 0.8 }}
                                                    >
                                                        {r.label}
                                                    </span>
                                                    <span
                                                        style={{
                                                            color: '#4ADE80',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {r.value}
                                                    </span>
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: '11px',
                                                        opacity: 0.6,
                                                        lineHeight: '1.3',
                                                        marginBottom:
                                                            index <
                                                            patternData.stats
                                                                .length -
                                                                1
                                                                ? 12
                                                                : 0,
                                                    }}
                                                >
                                                    {r.description}
                                                </div>
                                                {index <
                                                    patternData.stats.length -
                                                        1 && (
                                                    <div
                                                        style={{
                                                            height: '1px',
                                                            backgroundColor:
                                                                'rgba(255, 255, 255, 0.1)',
                                                            margin: '8px 0',
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
        );
    },
);

PatternVolatilityChart.displayName = 'PatternVolatilityChart';
